import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Plus, ShoppingBasket } from "lucide-react";
import { api, fmtPrice, FLAVOR_LABELS } from "@/lib/api";
import { useCompare } from "@/context/CompareContext";
import { useCart } from "@/context/CartContext";

export default function Compare() {
  const { skus, remove, clear } = useCompare();
  const { add } = useCart();
  const [products, setProducts] = useState([]);

  useEffect(() => {
    if (skus.length === 0) { setProducts([]); return; }
    api.get("/compare", { params: { skus: skus.join(",") } }).then((r) => setProducts(r.data));
  }, [skus.join(",")]);

  if (skus.length === 0) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-5 px-6 pt-24 text-center">
        <ShoppingBasket className="h-16 w-16 text-olive/30" />
        <h1 className="font-display text-2xl font-bold text-ink">لا توجد منتجات للمقارنة</h1>
        <p className="text-ink/55">أضف منتجات من المتجر عبر زر المقارنة لتراها هنا جنباً إلى جنب.</p>
        <Link to="/store" className="rounded-full bg-olive px-7 py-3 font-semibold text-ivory">تصفّح المتجر</Link>
      </div>
    );
  }

  const flavorKeys = ["sharp", "sweet", "salty", "herbal", "earthy", "fruity", "rich"];
  const rows = [
    { label: "المصدر الجغرافي", get: (p) => p.geo_source_ar || "—" },
    { label: "النوع", get: (p) => p.fingerprint?.type || "—" },
    { label: "الوزن", get: (p) => `${p.weight_grams} غم` },
    { label: "السعر", get: (p) => fmtPrice(p.price) },
    { label: "المكوّنات", get: (p) => (p.ingredients || []).map((i) => i.name_ar).join("، ") || "—" },
    { label: "طريقة الإنتاج", get: (p) => p.fingerprint?.production || "—" },
    { label: "الموسم", get: (p) => p.fingerprint?.season || "—" },
    { label: "مسببات الحساسية", get: (p) => (p.allergens?.length ? p.allergens.join("، ") : "لا يوجد") },
  ];

  return (
    <div className="min-h-screen pt-24">
      <div className="mx-auto max-w-7xl px-6 py-10">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="font-display text-3xl font-bold text-ink">قارن المنتجات</h1>
          <button onClick={clear} className="text-sm text-palred">مسح الكل</button>
        </div>

        <div className="overflow-x-auto">
          <table data-testid="compare-table" className="w-full min-w-[640px] border-separate border-spacing-2">
            <thead>
              <tr>
                <th className="w-32" />
                {products.map((p) => (
                  <th key={p.sku} className="align-top">
                    <div className="relative rounded-2xl border border-olive/10 bg-white p-3">
                      <button onClick={() => remove(p.sku)} className="absolute left-2 top-2 text-ink/40 hover:text-palred"><X className="h-4 w-4" /></button>
                      <Link to={`/product/${p.slug}`}><img src={p.images?.main} alt={p.name_ar} className="aspect-square w-full rounded-xl object-cover" /></Link>
                      <div className="mt-2 font-display text-sm font-semibold text-ink">{p.name_ar}</div>
                      <button data-testid={`compare-add-${p.sku}`} onClick={() => add(p)} className="mt-2 flex w-full items-center justify-center gap-1 rounded-full bg-olive py-2 text-xs font-medium text-ivory"><Plus className="h-3 w-3" /> أضف للسلة</button>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.label}>
                  <td className="rounded-xl bg-olive/5 px-3 py-2 text-sm font-medium text-ink/70">{row.label}</td>
                  {products.map((p) => (
                    <td key={p.sku} className="rounded-xl border border-olive/10 bg-white px-3 py-2 text-sm text-ink/75">{row.get(p)}</td>
                  ))}
                </tr>
              ))}
              <tr>
                <td className="rounded-xl bg-olive/5 px-3 py-2 text-sm font-medium text-ink/70">النكهات</td>
                {products.map((p) => (
                  <td key={p.sku} className="rounded-xl border border-olive/10 bg-white px-3 py-2">
                    <div className="space-y-1">
                      {flavorKeys.filter((k) => p.flavor?.[k] > 0).map((k) => (
                        <div key={k} className="flex items-center gap-2 text-xs">
                          <span className="w-12 text-ink/50">{FLAVOR_LABELS[k]}</span>
                          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-olive/10"><div className="h-full rounded-full bg-olive" style={{ width: `${(p.flavor[k] / 5) * 100}%` }} /></div>
                        </div>
                      ))}
                    </div>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
